import { DEFAULT_CURRENCY } from '@constants/currency';

import type { ILocation } from '@model/structureData';

import type { IEquipmentSetting } from '../model/equipmentSettingsList';
import type { IFacilityLevelActiveWithMultiplicationFactorValue } from '../model/multiplicationFactors';

interface GetFacilityLevelWithMultiplicationFactorValueParams {
  facilityLevelActive: ILocation[];
  facilitySettingsByKey: Record<string, IEquipmentSetting>;
  locationSettingsByKey: Record<string, IEquipmentSetting>;
  defaultMultiplicationFactor: number;
}

interface GetFacilityLevelWithMultiplicationFactorValueReturns {
  facilityLevelActiveWithMultiplicationFactorValue: IFacilityLevelActiveWithMultiplicationFactorValue[];
  allFacilitiesIds: Record<number, boolean>;
  totalFacilitiesCount: number;
}

export const getFacilityLevelActiveWithMultiplicationFactorValue = ({
  facilityLevelActive,
  facilitySettingsByKey,
  locationSettingsByKey,
  defaultMultiplicationFactor,
}: GetFacilityLevelWithMultiplicationFactorValueParams): GetFacilityLevelWithMultiplicationFactorValueReturns => {
  const allFacilitiesIds: Record<number, boolean> = {};
  let totalFacilitiesCount = 0;

  const transformFacilityLevelActiveNode = (
    facilityLevelActiveItem: ILocation,
    parentMultiplicationFactor: number,
  ): IFacilityLevelActiveWithMultiplicationFactorValue => {
    totalFacilitiesCount++;

    const isFacility = facilityLevelActiveItem.levelName === 'facility';
    const setting = isFacility
      ? facilitySettingsByKey[facilityLevelActiveItem.facilityId]
      : locationSettingsByKey[facilityLevelActiveItem.id];

    const multiplicationFactorValue =
      setting?.multiplicationFactorInCurrency ?? setting?.multiplicationFactor ?? parentMultiplicationFactor;

    allFacilitiesIds[facilityLevelActiveItem.id] = true;

    return {
      name: facilityLevelActiveItem.name,
      facilityId: facilityLevelActiveItem.facilityId,
      id: facilityLevelActiveItem.id,
      levelName: facilityLevelActiveItem.levelName,
      currentMultiplicationFactorValue: multiplicationFactorValue,
      settingId: setting?.id,
      currency: facilityLevelActiveItem.facility.currency || DEFAULT_CURRENCY,
      children:
        facilityLevelActiveItem.children?.map((child) =>
          transformFacilityLevelActiveNode(child, multiplicationFactorValue),
        ) || [],
    };
  };

  const facilityLevelActiveWithMultiplicationFactorValue = facilityLevelActive.map((facilityLevelItem) =>
    transformFacilityLevelActiveNode(facilityLevelItem, defaultMultiplicationFactor),
  );

  return { facilityLevelActiveWithMultiplicationFactorValue, allFacilitiesIds, totalFacilitiesCount };
};
